
import * as React from 'react';
import { Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { createLazyComponent } from "./components/LazyPageWrapper";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AppLayout } from "@/components/layout/AppLayout";

// Lazy load heavy page components for better performance
const LazyTestProcessing = createLazyComponent(() => import("./pages/TestProcessing"), "TestProcessing");
const LazyDashboard = createLazyComponent(() => import("./pages/Dashboard"), "Dashboard");
const LazyResetPassword = createLazyComponent(() => import("./pages/ResetPassword"), "ResetPassword");
const LazyResetPasswordConfirm = createLazyComponent(() => import("./pages/ResetPasswordConfirm"), "ResetPasswordConfirm");
const LazyPartners = createLazyComponent(() => import("./pages/Partners"), "Partners");
const LazyBills = createLazyComponent(() => import("./pages/Bills"), "Bills");
const LazyProcessing = createLazyComponent(() => import("./pages/Processing"), "Processing");
const LazyBillQueue = createLazyComponent(() => import("./pages/BillQueue"), "BillQueue");
const LazySuppliers = createLazyComponent(() => import("./pages/Suppliers"), "Suppliers");
const LazyReportsPage = createLazyComponent(() => import("./pages/Reports"), "Reports");
const LazyAnalytics = createLazyComponent(() => import("./pages/Analytics"), "Analytics");

// Keep frequently used/small components as regular imports for faster access
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import { MobileBillProcessing } from "./pages/MobileBillProcessing";
import { LazyComponentWrapper } from "./components/PerformanceOptimizations";
import Customers from "./pages/Customers";
import Sites from "./pages/Sites";
import Accounts from "./pages/Accounts";
import Entities from "./pages/Entities";
import Meters from "./pages/Meters";
import AIParser from "./pages/AIParser";
import AIAgents from "./pages/AIAgents";
import AutomationPage from "./pages/Automation";
import CollectorSandbox from "./pages/CollectorSandbox";
import ManualUploadBills from "./pages/ManualUploadBills";
import ReviewParsedBills from "./pages/ReviewParsedBills";
import BillDetails from "./pages/BillDetails";
import UnparsedBills from "./pages/UnparsedBills";
import ValidParsedBills from "./pages/ValidParsedBills";
import ManualParseDetail from "./pages/ManualParseDetail";
import DevParserQueue from "./pages/DevParserQueue";
import HeatMapPage from "./pages/HeatMapPage";
import ParserSandbox from "./pages/ParserSandbox";
import { PDFTestPage } from "./components/parser/PDFTestPage";
import CollectionWorkflows from "./pages/CollectionWorkflows";
import CollectionPaymentPlans from "./pages/CollectionPaymentPlans";
import CollectionActivities from "./pages/CollectionActivities";
import CollectionDashboard from "./pages/CollectionDashboard";
import CollectionContacts from "./pages/CollectionContacts";
import CollectionScheduling from "./pages/CollectionScheduling";
import SupplierRecommendations from "./pages/SupplierRecommendations";
import IntegrationHub from "./pages/IntegrationHub";
import QualityControl from "./pages/QualityControl";
import SupplierAnalytics from "./pages/SupplierAnalytics";
import RealTimeMonitoring from "./pages/RealTimeMonitoring";
import UserManagement from "./pages/UserManagement";
import DataExport from "./pages/DataExport";
import WorkflowAutomation from "./pages/WorkflowAutomation";
import AdvancedProcessing from "./pages/AdvancedProcessing";
import EnterpriseFeatures from "./pages/EnterpriseFeatures";
import { DashboardLayout } from "./layouts/DashboardLayout";
import PerformancePage from "./pages/Performance";
import PredictiveAnalyticsPage from "./pages/PredictiveAnalytics";
import IntegrationsPage from "./pages/Integrations";
import { PortalManagementPage as PortalManagement } from "./pages/PortalManagement";
import ValidationDashboard from "./pages/ValidationDashboard";
import ReviewDashboard from "./pages/ReviewDashboard";
import AILearning from "./pages/AILearning";
import PhaseManagement from "./pages/PhaseManagement";
import SmartRecommendations from "./pages/SmartRecommendations";
import ModelManagement from "./pages/ModelManagement";
import SystemMonitoring from "./pages/SystemMonitoring";
import ProcessingAnalytics from "./pages/ProcessingAnalytics";
import ProductionOptimization from "./pages/ProductionOptimization";
import API from "./pages/API";
import Security from "./pages/Security";
import AITestCenter from "./pages/AITestCenter";
import AdvancedExtraction from "./pages/AdvancedExtraction";
import ManualParserQueue from "./pages/ManualParserQueue";
import ReviewItem from "./pages/ReviewItem";
import ProductionReview from "./pages/ProductionReview";
import MobileApp from "./pages/MobileApp";
import SecurityCompliance from "./pages/SecurityCompliance";
import MobileFeatures from "./pages/MobileFeatures";
import NotFound from "./pages/NotFound";
import { SafeAuthProvider } from "./components/SafeAuthProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import { WorkspaceRedirect } from "./components/WorkspaceRedirect";
import { WorkspaceErrorBoundary } from '@/components/WorkspaceErrorBoundary';
import { RouteDebugger } from "./components/RouteDebugger";
import Index from "./pages/Index";
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, Navigate } from 'react-router-dom';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import CustomerPortal from '@/pages/tum/onboarding/CustomerPortal';
import { getOrCreateDefaultWorkspaceId } from '@/hooks/useDefaultWorkspace';
import AccountProfile from "@/pages/account/AccountProfile";
import AccountSecurity from "@/pages/account/AccountSecurity";
import AccountPassword from "@/pages/account/AccountPassword";
import NotificationsPage from "@/pages/inbox/NotificationsPage";
import MessagesPage from "@/pages/inbox/MessagesPage";
import RemindersPage from "@/pages/inbox/RemindersPage";
import WorkspaceTeam from "@/pages/workspace/WorkspaceTeam";
import WorkspaceBillingAdvanced from "@/pages/workspace/WorkspaceBillingAdvanced";
import WorkspaceIntegrations from "@/pages/workspace/WorkspaceIntegrations";
import WorkspaceFeatureToggles from "@/pages/workspace/WorkspaceFeatureToggles";
// Workspace management imports
import WorkspaceSettings from "./pages/workspace/WorkspaceSettings";
import OwnerConsole from "./pages/OwnerConsole";
import WorkspaceIsolationTester from "./pages/WorkspaceIsolationTester";
import HealthMonitor from "./pages/workspace/HealthMonitor";
import AccessControlEnhanced from "./pages/workspace/AccessControlEnhanced";
// New module pages
import APIAccess from "./pages/APIAccess";
import Storage from "./pages/Storage";
import Support from "./pages/Support";
import AccessControl from "./pages/AccessControl";
import AccessControlNew from "./pages/AccessControlNew";
import BulkOperations from "./pages/BulkOperations";
import ComplianceDashboard from "./pages/ComplianceDashboard";
import AuditLogViewer from "./pages/AuditLogViewer";
import SecurityMonitoring from "./pages/SecurityMonitoring";
import RoleManagement from "./pages/RoleManagement";
import MultiTenant from "./pages/MultiTenant";
import AcceptInvite from "./pages/invite/AcceptInvite";
import { SelectWorkspace } from "./pages/SelectWorkspace";
import { InviteAcceptWrapper } from "./components/InviteAcceptWrapper";
import InviteAccept from "./pages/InviteAccept";
import { TrialManagementPage } from "./pages/TrialManagementPage";
import EnterpriseAnalytics from "./pages/EnterpriseAnalytics";
import Phase6Dashboard from "./pages/Phase6Dashboard";
import DeveloperTools from "./pages/DeveloperTools";
import TumDashboard from "./pages/tum/TumDashboard";
import TumBills from "./pages/tum/TumBills";
import TumBillsList from "./pages/tum/bills/TumBillsList";
import TumBillDetail from "./pages/tum/bills/TumBillDetail";
import TumOnboarding from "./pages/tum/TumOnboarding";
import OnboardingModule from "./pages/tum/onboarding/OnboardingModule";
import OnboardingLayout from "./pages/tum/onboarding/OnboardingLayout";
import Stage1ProjectInitiation from "./pages/tum/onboarding/stages/Stage1ProjectInitiation";
import Stage2DataImportEnhanced from "./pages/tum/onboarding/stages/Stage2DataImportEnhanced";
import Stage3Validation from "./pages/tum/onboarding/stages/Stage3Validation";
import Stage4Mapping from "./pages/tum/onboarding/stages/Stage4Mapping";
import Stage5Review from "./pages/tum/onboarding/stages/Stage5Review";
import Stage6SignOff from "./pages/tum/onboarding/stages/Stage6SignOff";
import Stage7GoLive from "./pages/tum/onboarding/stages/Stage7GoLive";
import ChangeManagement from "./pages/tum/onboarding/ChangeManagement";
import TumBillJourney from "./pages/tum/TumBillJourney";
import TumProperties from "./pages/tum/TumProperties";
import TumTenants from "./pages/tum/TumTenants";
import TumPaymentReport from "./pages/tum/payments/TumPaymentReport";
import TumReportsExports from "./pages/tum/reports/TumReportsExports";
import TumReportsLibrary from "./pages/tum/reports/TumReportsLibrary";
import TumPortfolio from "./pages/tum/reports/TumPortfolio";
import TumSustainability from "./pages/tum/reports/TumSustainability";
import TumAnalytics from "./pages/tum/reports/TumAnalytics";
import TumNotifications from "./pages/tum/TumNotifications";
import TumIntegrations from "./pages/tum/TumIntegrations";
import IntegrationSettings from "./pages/settings/IntegrationSettings";
import TumParserIntegration from "./pages/tum/TumParserIntegration";
import TumVendorManagement from "./pages/tum/TumVendorManagement";
import { StripeProvider } from "@/contexts/StripeProvider";
import { WorkspaceProvider } from "./contexts/WorkspaceContext";

// DefaultWorkspaceRedirect component
function DefaultWorkspaceRedirect() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      try {
        const id = await getOrCreateDefaultWorkspaceId(user.id, user.email!);
        console.log('[DEFAULT_WS] Redirecting to workspace:', id);
        navigate(`/w/${id}/dashboard`, { replace: true });
        setDone(true);
      } catch (error) {
        console.error('[DEFAULT_WS] Error creating/finding workspace:', error);
        // If no workspaces are available, redirect to workspace selection
        if (error instanceof Error && error.message === 'NO_WORKSPACES_AVAILABLE') {
          console.log('[DEFAULT_WS] No workspaces available, redirecting to selection');
          navigate('/select-workspace', { replace: true });
        } else {
          console.error('[DEFAULT_WS] Unexpected error:', error);
          navigate('/select-workspace', { replace: true });
        }
        setDone(true);
      }
    })();
  }, [user?.id, navigate]);

  if (!user) return <Navigate to="/auth" replace />;
  return done ? null : <div className="p-6">Loading default workspace…</div>;
}

// WorkspaceRoute wrapper - CRITICAL: Provides workspace context inside the route
function WorkspaceRoute() {
  return (
    <WorkspaceErrorBoundary>
      <WorkspaceProvider>
        <AppLayout>
          <DashboardLayout />
        </AppLayout>
      </WorkspaceProvider>
    </WorkspaceErrorBoundary>
  );
}

// Environment setup - Supabase connection configured
console.log('✅ Using configured Supabase connection');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30,
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    },
    mutations: {
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    },
  },
});

const App: React.FC = () => {
  return (
    <>
      <Toaster />
      <SafeAuthProvider>
        <QueryClientProvider client={queryClient}>
          <StripeProvider>
            <ThemeProvider>
            <BrowserRouter>
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Auth />} />
            <Route path="/select-workspace" element={<SelectWorkspace />} />
            <Route path="/test-processing" element={<LazyTestProcessing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<LazyResetPassword />} />
            <Route path="/reset-password/confirm" element={<LazyResetPasswordConfirm />} />
            
            {/* Default workspace redirect */}
            <Route path="/dashboard" element={<DefaultWorkspaceRedirect />} />
            
            {/* Privacy & Terms routes */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            
            {/* Customer Portal - Public route for onboarding invitations */}
            <Route path="/onboarding/invite/:token" element={<CustomerPortal />} />

            {/* Invitation acceptance routes - MUST be public and before workspace routes */}
            {/* These routes handle workspace invitation acceptance */}
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/invite/accept/:token" element={<InviteAcceptWrapper routeType="path-param" />} />
            <Route path="/invite/accept" element={<InviteAcceptWrapper routeType="query-param" />} />

            {/* Global Account Routes */}
            <Route path="/account/profile" element={
              <ProtectedRoute>
                <AppLayout>
                  <AccountProfile />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/account/security" element={
              <ProtectedRoute>
                <AppLayout>
                  <AccountSecurity />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/account/password" element={
              <ProtectedRoute>
                <AppLayout>
                  <AccountPassword />
                </AppLayout>
              </ProtectedRoute>
            } />

            {/* Inbox Routes */}
            <Route path="/inbox/notifications" element={
              <ProtectedRoute>
                <AppLayout>
                  <NotificationsPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/inbox/messages" element={
              <ProtectedRoute>
                <AppLayout>
                  <MessagesPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/inbox/reminders" element={
              <ProtectedRoute>
                <AppLayout>
                  <RemindersPage />
                </AppLayout>
              </ProtectedRoute>
            } />

            {/* Alternative workspace routes (backward compatibility) - redirect to dashboard versions */}
            <Route path="/w/:workspaceId/settings" element={<WorkspaceRedirect path="settings" />} />
            <Route path="/w/:workspaceId/team" element={<WorkspaceRedirect path="team" />} />
            <Route path="/w/:workspaceId/billing" element={<WorkspaceRedirect path="billing" />} />
            <Route path="/w/:workspaceId/integrations" element={<WorkspaceRedirect path="integrations" />} />
            <Route path="/w/:workspaceId/api" element={<WorkspaceRedirect path="api" />} />

            {/* New workspace-aware routes - FIXED: Provider now inside the route */}
            <Route path="/w/:workspaceId/dashboard" element={
              <ProtectedRoute>
                <WorkspaceRoute />
              </ProtectedRoute>
            }>
                <Route index element={<LazyDashboard />} />
                <Route path="partners" element={<LazyPartners />} />
                <Route path="suppliers" element={<LazySuppliers />} />
                <Route path="customers" element={<Customers />} />
                <Route path="sites" element={<Sites />} />
                <Route path="accounts" element={<Accounts />} />
                <Route path="entities" element={<Entities />} />
                <Route path="meters" element={<Meters />} />
                <Route path="collector-sandbox" element={<CollectorSandbox />} />
                <Route path="bills/upload" element={<ManualUploadBills />} />
                <Route path="bills/review" element={<ReviewParsedBills />} />
                <Route path="bills/:billId/details" element={<BillDetails />} />
                <Route path="bills/unparsed" element={<UnparsedBills />} />
                <Route path="bills/valid" element={<ValidParsedBills />} />
                <Route path="parser/manual-queue" element={<ManualParserQueue />} />
                <Route path="parser/manual-queue/:jobId" element={<ManualParseDetail />} />
                <Route path="parser-sandbox" element={<ParserSandbox />} />
                <Route path="pdf-test" element={<PDFTestPage />} />
                <Route path="parser/dev" element={<DevParserQueue />} />
                <Route path="analytics/heatmap" element={<HeatMapPage />} />
                <Route path="collection" element={
                  <ProtectedRoute resource="bill_collection">
                    <CollectionDashboard />
                  </ProtectedRoute>
                } />
                <Route path="processing" element={<LazyProcessing />} />
                <Route path="mobile-processing" element={<MobileBillProcessing />} />
                <Route path="bill-queue" element={
                  <ProtectedRoute resource="bill_collection">
                    <LazyBillQueue />
                  </ProtectedRoute>
                } />
                <Route path="ai-parser" element={
                  <ProtectedRoute resource="ai_parser">
                    <AIParser />
                  </ProtectedRoute>
                } />
                <Route path="ai-agents" element={
                  <ProtectedRoute resource="ai_agents">
                    <AIAgents />
                  </ProtectedRoute>
                } />
                <Route path="automation" element={
                  <ProtectedRoute resource="automation">
                    <AutomationPage />
                  </ProtectedRoute>
                } />
                <Route path="bills" element={<LazyBills />} />
                <Route path="collection-activities" element={
                  <ProtectedRoute resource="bill_collection">
                    <CollectionActivities />
                  </ProtectedRoute>
                } />
                <Route path="collection-workflows" element={
                  <ProtectedRoute resource="bill_collection">
                    <CollectionWorkflows />
                  </ProtectedRoute>
                } />
                <Route path="collection/workflows" element={
                  <ProtectedRoute resource="collection">
                    <CollectionWorkflows />
                  </ProtectedRoute>
                } />
                <Route path="collection/payment-plans" element={
                  <ProtectedRoute resource="collection">
                    <CollectionPaymentPlans />
                  </ProtectedRoute>
                } />
                <Route path="collection/activities" element={
                  <ProtectedRoute resource="collection">
                    <CollectionActivities />
                  </ProtectedRoute>
                } />
                <Route path="collection/contacts" element={
                  <ProtectedRoute resource="collection">
                    <CollectionContacts />
                  </ProtectedRoute>
                } />
                <Route path="collection/scheduling" element={
                  <ProtectedRoute resource="collection">
                    <CollectionScheduling />
                  </ProtectedRoute>
                } />
                <Route path="reports" element={
                  <ProtectedRoute resource="reports">
                    <LazyReportsPage />
                  </ProtectedRoute>
                } />
                <Route path="performance" element={<PerformancePage />} />
                <Route path="portals" element={
                  <ProtectedRoute resource="collection">
                    <PortalManagement />
                  </ProtectedRoute>
                } />
                <Route path="analytics" element={
                  <ProtectedRoute resource="analytics">
                    <LazyAnalytics />
                  </ProtectedRoute>
                } />
                <Route path="enterprise-analytics" element={
                  <ProtectedRoute resource="enterprise_analytics">
                    <EnterpriseAnalytics />
                  </ProtectedRoute>
                } />
                <Route path="api" element={
                  <ProtectedRoute resource="api">
                    <APIAccess />
                  </ProtectedRoute>
                } />
                <Route path="storage" element={
                  <ProtectedRoute resource="storage">
                    <Storage />
                  </ProtectedRoute>
                } />
                <Route path="support" element={
                  <ProtectedRoute resource="support">
                    <Support />
                  </ProtectedRoute>
                } />
                <Route path="settings" element={<WorkspaceSettings />} />
                <Route path="settings/integrations" element={<IntegrationSettings />} />
                <Route path="team" element={<WorkspaceTeam />} />
                <Route path="billing" element={<WorkspaceBillingAdvanced />} />
                <Route path="integrations" element={<WorkspaceIntegrations />} />
                <Route path="admin/features" element={
                  <ProtectedRoute resource="workspace_admin">
                    <WorkspaceFeatureToggles />
                  </ProtectedRoute>
                } />
                <Route path="supplier-recommendations" element={<SupplierRecommendations />} />
                <Route path="integration-hub" element={<IntegrationHub />} />
                <Route path="integrations" element={
                  <ProtectedRoute resource="integrations">
                    <IntegrationsPage />
                  </ProtectedRoute>
                } />
                <Route path="trial" element={
                  <ProtectedRoute resource="trial_management">
                    <TrialManagementPage />
                  </ProtectedRoute>
                } />
                <Route path="access-control" element={
                  <ProtectedRoute ownerOnly>
                    <AccessControl />
                  </ProtectedRoute>
                } />
                <Route path="access-control-new" element={
                  <ProtectedRoute resource="workspace_admin">
                    <AccessControlNew />
                  </ProtectedRoute>
                } />
                <Route path="bulk-operations" element={
                  <ProtectedRoute resource="bulk_operations">
                    <BulkOperations />
                  </ProtectedRoute>
                } />
                <Route path="compliance" element={
                  <ProtectedRoute resource="compliance">
                    <ComplianceDashboard />
                  </ProtectedRoute>
                } />
                <Route path="audit-logs" element={
                  <ProtectedRoute resource="audit_logs">
                    <AuditLogViewer />
                  </ProtectedRoute>
                } />
                <Route path="phase6" element={
                  <ProtectedRoute resource="analytics">
                    <Phase6Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="developer-tools" element={
                  <ProtectedRoute resource="workspace_admin">
                    <DeveloperTools />
                  </ProtectedRoute>
                } />
                <Route path="security-monitoring" element={
                  <ProtectedRoute resource="security_monitoring">
                    <SecurityMonitoring />
                  </ProtectedRoute>
                } />
                <Route path="role-management" element={
                  <ProtectedRoute resource="role_management">
                    <RoleManagement />
                  </ProtectedRoute>
                } />
                <Route path="multi-tenant" element={
                  <ProtectedRoute resource="multi_tenant">
                    <MultiTenant />
                  </ProtectedRoute>
                } />
                <Route path="predictive-analytics" element={<PredictiveAnalyticsPage />} />
                <Route path="quality-control" element={<QualityControl />} />
                <Route path="supplier-analytics" element={<SupplierAnalytics />} />
                <Route path="real-time-monitoring" element={<RealTimeMonitoring />} />
                <Route path="user-management" element={<UserManagement />} />
                <Route path="data-export" element={<DataExport />} />
                <Route path="workflow-automation" element={
                  <ProtectedRoute resource="automation">
                    <WorkflowAutomation />
                  </ProtectedRoute>
                } />
                <Route path="advanced-processing" element={
                  <ProtectedRoute resource="processing">
                    <AdvancedProcessing />
                  </ProtectedRoute>
                } />
                <Route path="enterprise-features" element={<EnterpriseFeatures />} />
                <Route path="validation-dashboard" element={<ValidationDashboard />} />
                <Route path="review-dashboard" element={<ReviewDashboard />} />
                <Route path="ai-learning" element={
                  <ProtectedRoute resource="ai_parser">
                    <AILearning />
                  </ProtectedRoute>
                } />
                <Route path="smart-recommendations" element={<SmartRecommendations />} />
                <Route path="model-management" element={
                  <ProtectedRoute resource="ai_parser">
                    <ModelManagement />
                  </ProtectedRoute>
                } />
                <Route path="phases" element={<PhaseManagement />} />
                <Route path="security" element={<Security />} />
                <Route path="ai-test-center" element={
                  <ProtectedRoute resource="ai_parser">
                    <AITestCenter />
                  </ProtectedRoute>
                } />
                <Route path="advanced-extraction" element={
                  <ProtectedRoute resource="ai_parser">
                    <AdvancedExtraction />
                  </ProtectedRoute>
                } />
                <Route path="manual-parser-queue" element={<ManualParserQueue />} />
                <Route path="mobile-app" element={<MobileApp />} />
                <Route path="mobile-features" element={<MobileFeatures />} />
                <Route path="security-compliance" element={<SecurityCompliance />} />
                <Route path="system-monitoring" element={<SystemMonitoring />} />
                <Route path="processing-analytics" element={
                  <ProtectedRoute resource="processing">
                    <ProcessingAnalytics />
                  </ProtectedRoute>
                } />
                <Route path="production-optimization" element={
                  <ProtectedRoute resource="processing">
                    <ProductionOptimization />
                  </ProtectedRoute>
                } />
                <Route path="review/:queueItemId" element={<ReviewItem />} />
                <Route path="production-review/:queueItemId" element={<ProductionReview />} />
                
                {/* Workspace Management Routes */}
                <Route path="workspace-settings" element={
                  <ProtectedRoute resource="workspace_admin">
                    <WorkspaceSettings />
                  </ProtectedRoute>
                } />
                <Route path="tum" element={<TumDashboard />} />
                <Route path="tum/vendors" element={<TumVendorManagement />} />
                <Route path="tum/onboarding" element={<OnboardingModule />} />
                <Route path="tum/onboarding/change-management" element={<ChangeManagement />} />
                <Route path="tum/onboarding/project/:projectId/change-management" element={<ChangeManagement />} />
                <Route path="tum/onboarding/project/:projectId" element={<OnboardingLayout />}>
                  <Route index element={<Stage1ProjectInitiation />} />
                  <Route path="stage-1" element={<Stage1ProjectInitiation />} />
                  <Route path="stage-2" element={<Stage2DataImportEnhanced />} />
                  <Route path="stage-3" element={<Stage3Validation />} />
                  <Route path="stage-4" element={<Stage4Mapping />} />
                  <Route path="stage-5" element={<Stage5Review />} />
                  <Route path="stage-6" element={<Stage6SignOff />} />
                  <Route path="stage-7" element={<Stage7GoLive />} />
                </Route>
                <Route path="tum/bill-journey" element={<TumBillJourney />} />
                {/* Properties sub-routes - more specific routes first */}
                <Route path="tum/properties/bills/:billId" element={<TumBillDetail />} />
                <Route path="tum/properties/bills" element={<TumBillsList />} />
                <Route path="tum/properties/tenants" element={<TumTenants />} />
                <Route path="tum/properties/vendors" element={<TumVendorManagement />} />
                <Route path="tum/properties" element={<TumProperties />} />
                {/* Reports & Analytics sub-routes - more specific routes first */}
                <Route path="tum/reports/library" element={<TumReportsLibrary />} />
                <Route path="tum/reports/portfolio" element={<TumPortfolio />} />
                <Route path="tum/reports/sustainability" element={<TumSustainability />} />
                <Route path="tum/reports/analytics" element={<TumAnalytics />} />
                <Route path="tum/reports" element={<TumReportsExports />} />
                {/* Legacy routes for backward compatibility */}
                <Route path="tum/bills/:billId" element={<TumBillDetail />} />
                <Route path="tum/bills" element={<TumBillsList />} />
                <Route path="tum/tenants" element={<TumTenants />} />
                <Route path="tum/payment-report" element={<TumPaymentReport />} />
                <Route path="tum/notifications" element={<TumNotifications />} />
                <Route path="tum/integrations" element={<TumIntegrations />} />
                <Route path="tum/parser-integration" element={
                  <ProtectedRoute>
                    <TumParserIntegration />
                  </ProtectedRoute>
                } />
            </Route>
              
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
         </BrowserRouter>
        </ThemeProvider>
        </StripeProvider>
      </QueryClientProvider>
    </SafeAuthProvider>
    </>
  );
};

export default App;
